// AI story generation flow: Form → Generating (SSE) → Preview → Approve/Reject

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check, X, ChevronRight, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/molecules/Card';
import { useGenerateStory } from '@/hooks/useGenerateStory';
import {
  saveGeneratedStory,
  generateStoryBackgroundImage,
  type ActivityIntegrityError,
} from '@/services/api/backoffice.api';
import { ApiError } from '@/services/api/client';
import { IntegrityRetryModal } from '@/components/molecules/IntegrityRetryModal';
import { buildRoute, ROUTES } from '@/constants/routes';
import styles from './GenerateStoryPage.module.scss';

/** Devuelve el mensaje en lenguaje humano de un error de integridad. */
function humanizeActivityErrors(errors: ActivityIntegrityError[]): string[] {
  return errors.flatMap((e) => {
    const ref =
      e.index === -1
        ? ''
        : `Actividad ${e.index + 1}${e.type ? ` (${e.type})` : ''}: `;
    return e.errors.map((msg) => `${ref}${msg}`);
  });
}

const LEVELS = [
  { label: 'A1 — Beginner', value: 'A1' },
  { label: 'A2 — Elementary', value: 'A2' },
  { label: 'B1 — Intermediate', value: 'B1' },
  { label: 'B2 — Upper-Intermediate', value: 'B2' },
  { label: 'C1 — Advanced', value: 'C1' },
  { label: 'C2 — Mastery', value: 'C2' },
];

export function GenerateStoryPage() {
  const navigate = useNavigate();
  const {
    status,
    progressMessages,
    generatedStory: generatedStory,
    error,
    integrityErrors,
    auditingActivities,
    audioScriptErrors,
    auditingAudioScript,
    generate,
    retryIntegrityWithAI,
    retryAudioScriptWithAI,
    setIntegrityErrorsFromSave,
    clearIntegrityErrors,
    clearAudioScriptErrors,
    reset,
  } = useGenerateStory();

  const [topic, setTopic] = useState('');
  const [level, setLevel] = useState('B1');
  const [savingPhase, setSavingPhase] = useState<'idle' | 'saving' | 'image'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedStoryId, setSavedStoryId] = useState<string | null>(null);
  const [integrityModalOpen, setIntegrityModalOpen] = useState(false);
  const [audioScriptModalOpen, setAudioScriptModalOpen] = useState(false);
  const isSaving = savingPhase !== 'idle';

  // Sincroniza la apertura del modal con la presencia de errores: cuando
  // llegan errores se abre solo; cuando un retry exitoso los vacía, se cierra
  // para que el editor pueda continuar guardando sin clicar Cerrar.
  useEffect(() => {
    setIntegrityModalOpen(integrityErrors.length > 0);
  }, [integrityErrors]);

  useEffect(() => {
    setAudioScriptModalOpen(audioScriptErrors.length > 0);
  }, [audioScriptErrors]);

  const handleGenerate = () => {
    if (!topic.trim()) return;
    void generate(topic.trim(), level, '');
  };

  const generateImage = async (storyId: string) => {
    setSavingPhase('image');
    setSaveError(null);
    try {
      await generateStoryBackgroundImage(storyId);
      navigate(buildRoute(ROUTES.BACKOFFICE.STORY_EDIT, { id: storyId }));
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Error desconocido';
      setSaveError(`Historia guardada, pero la generación de imagen falló: ${detail}`);
      setSavingPhase('idle');
    }
  };

  const handleApprove = async () => {
    if (!generatedStory) return;
    setSaveError(null);
    setSavingPhase('saving');
    try {
      const saved = await saveGeneratedStory(generatedStory);
      if (saved.vocabResourceId) {
        console.info(`[KC] Vocabulary resource created: ${saved.vocabResourceId}`);
      }
      setSavedStoryId(saved.id);
      await generateImage(saved.id);
    } catch (err) {
      // Si el backend rechaza por integridad, abrimos el modal con los
      // detalles para que el editor pueda pedir un retry con IA.
      if (
        err instanceof ApiError &&
        err.status === 400 &&
        err.data &&
        typeof err.data === 'object' &&
        Array.isArray((err.data as { details?: unknown }).details)
      ) {
        const details = (err.data as { details: ActivityIntegrityError[] }).details;
        setIntegrityErrorsFromSave(details);
        setSavingPhase('idle');
        return;
      }
      setSaveError(err instanceof Error ? err.message : 'Error al guardar la historia');
      setSavingPhase('idle');
    }
  };

  const handleCloseIntegrityModal = (open: boolean) => {
    setIntegrityModalOpen(open);
    if (!open) clearIntegrityErrors();
  };

  const handleCloseAudioScriptModal = (open: boolean) => {
    setAudioScriptModalOpen(open);
    if (!open) clearAudioScriptErrors();
  };

  const handleRetryImage = () => {
    if (savedStoryId) void generateImage(savedStoryId);
  };

  const handleSkipImage = () => {
    if (savedStoryId) {
      navigate(buildRoute(ROUTES.BACKOFFICE.STORY_EDIT, { id: savedStoryId }));
    }
  };

  const handleReject = () => {
    reset();
    setTopic('');
  };

  // ── Formulario ──
  if (status === 'idle' || status === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.page__header}>
          <Sparkles size={24} className={styles.page__icon} />
          <div>
            <h1>Generar historia con IA</h1>
            <p className={styles.page__subtitle}>
              La IA generará una historia de tipo Listening siguiendo las reglas del MCP.
            </p>
          </div>
        </div>

        <Card className={styles.form}>
          <div className={styles.form__field}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={styles.form__label}>Tema de la historia</label>
            <Input
              placeholder="Ej: Remote Work, Negotiation Skills, Mental Health..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
            />
          </div>

          <div className={styles.form__field}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={styles.form__label}>Nivel CEFR</label>
            <Select
              value={level}
              onValueChange={setLevel}
              options={LEVELS}
              placeholder="Seleccionar nivel"
            />
          </div>

          {status === 'error' && error && (
            <div className={styles.errorBox}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className={styles.form__actions}>
            <Button
              onClick={() => navigate(ROUTES.BACKOFFICE.STORIES)}
              variant="ghost"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={!topic.trim()}
              rightIcon={<ChevronRight size={16} />}
            >
              Generar
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Generando ──
  if (status === 'generating') {
    return (
      <div className={styles.page}>
        <div className={styles.page__header}>
          <Sparkles size={24} className={styles.page__icon} />
          <div>
            <h1>Generando historia...</h1>
            <p className={styles.page__subtitle}>
              Esto puede tardar 30–60 segundos. No cierres esta página.
            </p>
          </div>
        </div>

        <Card className={styles.progress}>
          <div className={styles.progress__spinner} aria-hidden="true" />
          <ul className={styles.progress__list} aria-live="polite">
            {progressMessages.map((msg, i) => (
              <li key={i} className={styles.progress__item}>
                <Check size={14} className={styles.progress__check} />
                {msg}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    );
  }

  // ── Preview ──
  if (status === 'complete' && generatedStory) {
    const audioScript = generatedStory.resources?.find(
      (r) => r.title === 'Audio Script'
    );
    const vocabulary = generatedStory.resources?.find(
      (r) => r.title === 'Vocabulary'
    );
    const practice = generatedStory.resources?.find(
      (r) => r.title === 'Practice'
    );

    return (
      <div className={styles.page}>
        <IntegrityRetryModal
          open={integrityModalOpen}
          onOpenChange={handleCloseIntegrityModal}
          errors={humanizeActivityErrors(integrityErrors)}
          onRetry={() => void retryIntegrityWithAI()}
          retrying={auditingActivities}
        />
        <IntegrityRetryModal
          open={audioScriptModalOpen}
          onOpenChange={handleCloseAudioScriptModal}
          title="El audio script viene sin párrafos"
          description="La IA generó el audio script como un único bloque de texto, sin saltos de línea entre beats. Esto afecta a las pausas del audio y del karaoke. Puedes pedirle a la IA que lo reformatee sin tocar las palabras, o cerrar este aviso y editarlo a mano."
          errors={audioScriptErrors}
          onRetry={() => void retryAudioScriptWithAI()}
          retrying={auditingAudioScript}
        />
        <div className={styles.page__header}>
          <Check size={24} className={styles.page__iconSuccess} />
          <div>
            <h1>Vista previa de la historia</h1>
            <p className={styles.page__subtitle}>
              Revisa el contenido generado. Al aprobar se guardan: la historia + un recurso de vocabulario vinculado (ambos como borrador).
            </p>
          </div>
        </div>

        <Card className={styles.preview__meta}>
          <h2 className={styles.preview__title}>{generatedStory.title}</h2>
          <p className={styles.preview__description}>{generatedStory.description}</p>
          <div className={styles.preview__badges}>
            <Badge>{generatedStory.level}</Badge>
            <Badge variant="default">{generatedStory.category}</Badge>
            <Badge variant="default">Listening</Badge>
            <Badge variant="default">~{generatedStory.estimatedTime} min</Badge>
            <Badge variant="default">{generatedStory.xpReward} XP</Badge>
            <Badge variant="default">{generatedStory.activities?.length ?? 0} actividades</Badge>
          </div>
        </Card>

        {audioScript && (
          <Card className={styles.preview__section}>
            <h3 className={styles.preview__sectionTitle}>Audio Script</h3>
            <p className={styles.preview__text}>{audioScript.content}</p>
          </Card>
        )}

        {vocabulary && (
          <Card className={styles.preview__section}>
            <h3 className={styles.preview__sectionTitle}>Vocabulario</h3>
            <pre className={styles.preview__pre}>{vocabulary.content}</pre>
          </Card>
        )}

        {practice && (
          <Card className={styles.preview__section}>
            <h3 className={styles.preview__sectionTitle}>Práctica</h3>
            <pre className={styles.preview__pre}>{practice.content}</pre>
          </Card>
        )}

        <Card className={styles.preview__section}>
          <h3 className={styles.preview__sectionTitle}>
            Actividades ({generatedStory.activities?.length ?? 0})
          </h3>
          <ul className={styles.preview__activityList}>
            {generatedStory.activities?.map((act, i) => (
              <li key={i} className={styles.preview__activityItem}>
                <Badge variant="default">{String(act.type ?? '').replace(/_/g, ' ')}</Badge>
                <span className={styles.preview__activityQuestion}>
                  {String(act.question ?? '')}
                </span>
              </li>
            ))}
          </ul>
        </Card>

        {saveError && (
          <div className={styles.errorBox}>
            <AlertCircle size={16} />
            <span>{saveError}</span>
          </div>
        )}
        <div className={styles.preview__actions}>
          {savedStoryId && saveError ? (
            <>
              <Button variant="ghost" onClick={handleSkipImage}>
                Continuar sin imagen
              </Button>
              <Button leftIcon={<RefreshCw size={16} />} onClick={handleRetryImage}>
                Reintentar imagen
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                leftIcon={<X size={16} />}
                onClick={handleReject}
                disabled={isSaving}
              >
                Rechazar y regenerar
              </Button>
              <Button
                variant="ghost"
                leftIcon={<RefreshCw size={16} />}
                onClick={() => generate(topic, level, '')}
                disabled={isSaving}
              >
                Regenerar
              </Button>
              <Button
                leftIcon={<Check size={16} />}
                onClick={handleApprove}
                disabled={isSaving}
              >
                {savingPhase === 'saving'
                  ? 'Guardando historia y audios…'
                  : savingPhase === 'image'
                    ? 'Generando imagen…'
                    : 'Aprobar y guardar'}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export default GenerateStoryPage;
